/**
 * PageHeroDecor — 各ページのヒーローセクション用幾何学装飾
 * variant でページの内容・テーマに合わせた装飾を切り替える
 *
 * amber  → 考え方・できること（意味・言語・翻訳テーマ）
 * gold   → 代表メッセージ・会社情報（深さ・信頼・蓄積テーマ）
 * teal   → 進め方（プロセス・フロー・変換テーマ）
 * cases  → 事例（実績・記録・証拠テーマ）
 * contact→ お問い合わせ（収束・焦点・対話テーマ）
 */

interface PageHeroDecorProps {
  variant?: 'amber' | 'gold' | 'teal' | 'cases' | 'contact';
}

export default function PageHeroDecor({ variant = 'amber' }: PageHeroDecorProps) {

  // ── amber: 考え方・できること — 意味・言語・翻訳テーマ ──
  if (variant === 'amber') {
    return (
      <>
        {/* 右上の同心円（言語が広がる波紋） */}
        <div className="absolute -top-12 -right-12 w-[180px] h-[180px] lg:w-[380px] lg:h-[380px] rounded-full border border-accent-amber/20 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[280px] h-[280px] lg:w-[560px] lg:h-[560px] rounded-full border border-accent-gold/13 pointer-events-none hidden sm:block" />
        <div className="absolute -top-28 -right-28 w-[380px] h-[380px] lg:w-[740px] lg:h-[740px] rounded-full border border-accent-amber/7 pointer-events-none hidden md:block" />

        {/* 右上の菱形チェーン（意味の核） */}
        <div className="absolute top-8 right-8 lg:top-16 lg:right-20 w-8 h-8 lg:w-16 lg:h-16 rotate-45 border-2 border-accent-amber/36 pointer-events-none" />
        <div className="absolute top-12 right-12 lg:top-24 lg:right-28 w-4 h-4 lg:w-8 lg:h-8 rotate-45 border border-accent-gold/42 pointer-events-none" />
        <div className="absolute top-14 right-14 lg:top-28 lg:right-32 w-2 h-2 lg:w-4 lg:h-4 rotate-45 bg-accent-amber/28 pointer-events-none" />

        {/* 右上ドットグリッド（散らばった言語の断片） */}
        <div className="absolute top-0 right-0 w-32 h-32 lg:w-60 lg:h-60 pointer-events-none overflow-hidden">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.22) 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />
        </div>

        {/* 「意味の断片」— 漢字の散らばり（PC） */}
        {[
          { char: '意', x: 'calc(100% - 120px)', y: '35%', size: 10, color: 'rgba(212,122,46,0.16)' },
          { char: '味', x: 'calc(100% - 80px)', y: '55%', size: 8, color: 'rgba(201,168,76,0.14)' },
          { char: '語', x: 'calc(100% - 150px)', y: '65%', size: 9, color: 'rgba(74,158,142,0.13)' },
          { char: '翻', x: 'calc(100% - 60px)', y: '72%', size: 7, color: 'rgba(212,122,46,0.11)' },
        ].map((item, i) => (
          <div
            key={i}
            className="absolute pointer-events-none hidden lg:block"
            style={{ left: item.x, top: item.y, fontFamily: 'serif', fontSize: item.size, color: item.color }}
          >
            {item.char}
          </div>
        ))}

        {/* 左下の装飾 */}
        <div className="absolute bottom-4 left-6 lg:bottom-8 lg:left-16 w-6 h-6 lg:w-10 lg:h-10 rotate-45 border border-accent-amber/28 pointer-events-none" />
        <div className="absolute bottom-8 left-12 lg:bottom-16 lg:left-28 w-3 h-3 lg:w-5 lg:h-5 rounded-full bg-accent-gold/22 pointer-events-none" />

        {/* 縦ライン（右端） */}
        <div className="absolute right-16 top-1/3 pointer-events-none hidden lg:block" style={{ width: 1.5, height: 80, background: 'linear-gradient(to bottom, transparent, rgba(212,122,46,0.32), transparent)', transform: 'rotate(20deg)' }} />
        <div className="absolute right-24 top-1/4 pointer-events-none hidden lg:block" style={{ width: 1, height: 60, background: 'linear-gradient(to bottom, transparent, rgba(201,168,76,0.25), transparent)', transform: 'rotate(-15deg)' }} />
      </>
    );
  }

  // ── gold: 代表メッセージ・会社情報 — 深さ・信頼・蓄積テーマ ──
  if (variant === 'gold') {
    return (
      <>
        {/* 右上の「積み重なる同心円」— 信頼の蓄積 */}
        <div className="absolute -top-10 -right-10 w-[160px] h-[160px] lg:w-[340px] lg:h-[340px] rounded-full border-2 border-accent-gold/22 pointer-events-none" />
        <div className="absolute -top-18 -right-18 w-[260px] h-[260px] lg:w-[520px] lg:h-[520px] rounded-full border border-accent-amber/14 pointer-events-none hidden sm:block" />
        <div className="absolute -top-26 -right-26 w-[360px] h-[360px] lg:w-[700px] lg:h-[700px] rounded-full border border-accent-gold/8 pointer-events-none hidden md:block" />
        {/* 中心点（信頼の核） */}
        <div className="absolute top-0 right-0 w-3 h-3 rounded-full bg-accent-gold/40 pointer-events-none" />
        <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-accent-gold/60 pointer-events-none" />

        {/* 右上の「深さを示す菱形」— 入れ子構造 */}
        <div className="absolute top-10 right-10 lg:top-20 lg:right-24 pointer-events-none">
          <div className="absolute w-10 h-10 lg:w-18 lg:h-18 border-2 border-accent-gold/38 rotate-45" style={{ width: 40, height: 40 }} />
          <div className="absolute border border-accent-amber/44 rotate-45" style={{ width: 22, height: 22, left: 9, top: 9 }} />
          <div className="absolute rounded-full bg-accent-gold/42" style={{ width: 7, height: 7, left: 17, top: 17 }} />
        </div>

        {/* 右上ドットグリッド */}
        <div className="absolute top-0 right-0 w-28 h-28 lg:w-56 lg:h-56 pointer-events-none overflow-hidden">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.24) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
        </div>

        {/* 「時間の積み重なり」を示す水平線群（右中央） */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className="mb-3"
              style={{
                width: 20 + i * 8,
                height: 1,
                background: `rgba(${i % 2 === 0 ? '201,168,76' : '212,122,46'},${0.28 - i * 0.03})`,
              }}
            />
          ))}
        </div>

        {/* 左下の装飾 */}
        <div className="absolute bottom-6 left-8 lg:bottom-12 lg:left-20 w-5 h-5 lg:w-10 lg:h-10 rotate-45 border border-accent-gold/26 pointer-events-none" />
        <div className="absolute bottom-10 left-14 lg:bottom-20 lg:left-32 w-2.5 h-2.5 lg:w-5 lg:h-5 rounded-full bg-accent-amber/22 pointer-events-none" />

        {/* 縦ライン */}
        <div className="absolute right-20 top-1/4 pointer-events-none hidden lg:block" style={{ width: 1, height: 100, background: 'linear-gradient(to bottom, transparent, rgba(201,168,76,0.30), transparent)', transform: 'rotate(10deg)' }} />
      </>
    );
  }

  // ── teal: 進め方 — プロセス・フロー・変換テーマ ──
  if (variant === 'teal') {
    return (
      <>
        {/* 右上の「フロー同心円」— プロセスの広がり */}
        <div className="absolute -top-12 -right-12 w-[180px] h-[180px] lg:w-[360px] lg:h-[360px] rounded-full border border-accent-teal/20 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[280px] h-[280px] lg:w-[540px] lg:h-[540px] rounded-full border border-accent-amber/12 pointer-events-none hidden sm:block" />
        <div className="absolute -top-28 -right-28 w-[380px] h-[380px] lg:w-[720px] lg:h-[720px] rounded-full border border-accent-teal/7 pointer-events-none hidden md:block" />

        {/* 右上の「変換ゲート菱形」 */}
        <div className="absolute top-8 right-8 lg:top-16 lg:right-20 pointer-events-none">
          <div className="absolute w-8 h-8 lg:w-16 lg:h-16 border-2 border-accent-teal/35 rotate-45" style={{ width: 32, height: 32 }} />
          <div className="absolute border border-accent-amber/40 rotate-45" style={{ width: 16, height: 16, left: 8, top: 8 }} />
          <div className="absolute rounded-full bg-accent-teal/40" style={{ width: 5, height: 5, left: 14, top: 14 }} />
        </div>

        {/* 右上ドットグリッド */}
        <div className="absolute top-0 right-0 w-32 h-32 lg:w-60 lg:h-60 pointer-events-none overflow-hidden">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(74,158,142,0.22) 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />
        </div>

        {/* 「フロー矢印」— プロセスの流れ（右中央） */}
        <div className="absolute right-8 top-1/3 pointer-events-none hidden lg:block">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-1 mb-5">
              <div className="w-6 h-px" style={{ background: `rgba(${i % 2 === 0 ? '74,158,142' : '212,122,46'},${0.25 - i * 0.03})` }} />
              <div className="w-0 h-0" style={{ borderTop: '3px solid transparent', borderBottom: '3px solid transparent', borderLeft: `5px solid rgba(${i % 2 === 0 ? '74,158,142' : '212,122,46'},${0.25 - i * 0.03})` }} />
            </div>
          ))}
        </div>

        {/* 左下の装飾 */}
        <div className="absolute bottom-4 left-6 lg:bottom-8 lg:left-16 w-6 h-6 lg:w-12 lg:h-12 rotate-45 border border-accent-teal/25 pointer-events-none" />
        <div className="absolute bottom-8 left-12 lg:bottom-16 lg:left-28 w-3 h-3 lg:w-6 lg:h-6 rounded-full bg-accent-amber/22 pointer-events-none" />

        {/* 縦ライン */}
        <div className="absolute right-16 top-1/4 pointer-events-none hidden lg:block" style={{ width: 1.5, height: 90, background: 'linear-gradient(to bottom, transparent, rgba(74,158,142,0.30), transparent)', transform: 'rotate(15deg)' }} />
        <div className="absolute right-24 top-1/3 pointer-events-none hidden lg:block" style={{ width: 1, height: 60, background: 'linear-gradient(to bottom, transparent, rgba(212,122,46,0.22), transparent)', transform: 'rotate(-20deg)' }} />
      </>
    );
  }

  // ── cases: 事例 — 実績・記録・証拠テーマ ──
  if (variant === 'cases') {
    return (
      <>
        {/* 右上の「記録を示す格子」— 実績の積み重なり */}
        <div className="absolute top-0 right-0 w-32 h-32 lg:w-64 lg:h-64 pointer-events-none overflow-hidden">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.20) 1.5px, transparent 1.5px)', backgroundSize: '14px 14px' }} />
        </div>

        {/* 右上の「実績を示す同心円」 */}
        <div className="absolute -top-10 -right-10 w-[160px] h-[160px] lg:w-[320px] lg:h-[320px] rounded-full border border-accent-amber/18 pointer-events-none" />
        <div className="absolute -top-18 -right-18 w-[260px] h-[260px] lg:w-[500px] lg:h-[500px] rounded-full border border-accent-gold/12 pointer-events-none hidden sm:block" />

        {/* 右上の菱形 */}
        <div className="absolute top-8 right-8 lg:top-14 lg:right-18 pointer-events-none">
          <div className="absolute w-8 h-8 lg:w-14 lg:h-14 border-2 border-accent-amber/34 rotate-45" style={{ width: 32, height: 32 }} />
          <div className="absolute border border-accent-gold/40 rotate-45" style={{ width: 16, height: 16, left: 8, top: 8 }} />
          <div className="absolute rounded-full bg-accent-amber/38" style={{ width: 5, height: 5, left: 14, top: 14 }} />
        </div>

        {/* 「記録の積み重なり」を示す水平線群 */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
          {[0, 1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="mb-2.5"
              style={{
                width: 16 + i * 10,
                height: 1,
                background: `rgba(${i % 2 === 0 ? '212,122,46' : '201,168,76'},${0.26 - i * 0.03})`,
              }}
            />
          ))}
        </div>

        {/* 左下の装飾 */}
        <div className="absolute bottom-4 left-6 lg:bottom-8 lg:left-16 w-5 h-5 lg:w-10 lg:h-10 rotate-45 border border-accent-amber/26 pointer-events-none" />
        <div className="absolute bottom-8 left-12 lg:bottom-16 lg:left-28 w-2.5 h-2.5 lg:w-5 lg:h-5 rounded-full bg-accent-gold/22 pointer-events-none" />

        {/* 縦ライン */}
        <div className="absolute right-18 top-1/3 pointer-events-none hidden lg:block" style={{ width: 1.5, height: 80, background: 'linear-gradient(to bottom, transparent, rgba(212,122,46,0.28), transparent)', transform: 'rotate(18deg)' }} />
      </>
    );
  }

  // ── contact: お問い合わせ — 収束・焦点・対話テーマ ──
  return (
    <>
      {/* 右上の「収束する同心円」— 焦点に向かう */}
      <div className="absolute -top-10 -right-10 w-[160px] h-[160px] lg:w-[320px] lg:h-[320px] rounded-full border border-accent-teal/20 pointer-events-none" />
      <div className="absolute -top-18 -right-18 w-[260px] h-[260px] lg:w-[500px] lg:h-[500px] rounded-full border border-accent-amber/13 pointer-events-none hidden sm:block" />
      {/* 焦点（中心点） */}
      <div className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-accent-teal/35 pointer-events-none" />
      <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-accent-teal/50 pointer-events-none" />

      {/* 右上の「収束する菱形」 */}
      <div className="absolute top-8 right-8 lg:top-16 lg:right-20 pointer-events-none">
        <div className="absolute w-8 h-8 lg:w-16 lg:h-16 border-2 border-accent-teal/32 rotate-45" style={{ width: 32, height: 32 }} />
        <div className="absolute border border-accent-amber/38 rotate-45" style={{ width: 16, height: 16, left: 8, top: 8 }} />
        <div className="absolute rounded-full bg-accent-teal/42" style={{ width: 5, height: 5, left: 14, top: 14 }} />
      </div>

      {/* 右上ドットグリッド */}
      <div className="absolute top-0 right-0 w-28 h-28 lg:w-56 lg:h-56 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(74,158,142,0.20) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
      </div>

      {/* 「収束する矢印」— 対話に向かう（右中央） */}
      <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-1 mb-4">
            <div style={{ width: 32 - i * 6, height: 1, background: `rgba(${i === 0 ? '74,158,142' : i === 1 ? '212,122,46' : '201,168,76'},${0.26 - i * 0.04})` }} />
            <div className="rounded-full" style={{ width: 4, height: 4, backgroundColor: `rgba(${i === 0 ? '74,158,142' : i === 1 ? '212,122,46' : '201,168,76'},${0.32 - i * 0.05})` }} />
          </div>
        ))}
      </div>

      {/* 左下の装飾 */}
      <div className="absolute bottom-4 left-6 lg:bottom-8 lg:left-16 w-6 h-6 lg:w-10 lg:h-10 rotate-45 border border-accent-teal/24 pointer-events-none" />
      <div className="absolute bottom-8 left-12 lg:bottom-16 lg:left-28 w-3 h-3 lg:w-5 lg:h-5 rounded-full bg-accent-amber/22 pointer-events-none" />

      {/* 縦ライン */}
      <div className="absolute right-16 top-1/3 pointer-events-none hidden lg:block" style={{ width: 1.5, height: 80, background: 'linear-gradient(to bottom, transparent, rgba(74,158,142,0.28), transparent)', transform: 'rotate(20deg)' }} />
    </>
  );
}
